import React, { useState, useMemo } from 'react';
import { X, Award, Sparkles } from 'lucide-react';
import { scorePokemon } from '../utils/pokemonScorer';

export default function GradeModal({ isOpen, onClose, selectedMons = [], pokedex }) {
  const [role, setRole] = useState('berry');

  const ROLES = [
    { id: 'berry', label: 'Berry' },
    { id: 'ingredient', label: 'Ingredient' },
    { id: 'skill', label: 'Skill' }
  ];

  // Recalculate grades whenever the role tab, selected mons, or pokedex change
  const gradedMons = useMemo(() => {
    if (!isOpen || !selectedMons || selectedMons.length === 0 || !pokedex) return [];
    
    const graded = selectedMons.map(mon => {
      const scoreInfo = scorePokemon(mon, pokedex, role);
      return {
        mon,
        species: pokedex[mon.species_id],
        scoreInfo
      };
    }).filter(item => item.scoreInfo); // Filter out any parsing errors

    // Sort by absolute production score to order the list neatly
    graded.sort((a, b) => b.scoreInfo.productionScore - a.scoreInfo.productionScore);

    return graded;
  }, [isOpen, selectedMons, pokedex, role]);

  const sections = [
    { 
      key: 'invest', 
      label: 'Invest', 
      items: gradedMons.filter(item => item.scoreInfo.grade === 'invest'), 
      themeClasses: 'bg-green-50 text-green-600 border-green-200',
      tagClasses: 'bg-green-100/60 text-green-700 border-green-200'
    },
    { 
      key: 'watch', 
      label: 'Watch', 
      items: gradedMons.filter(item => item.scoreInfo.grade === 'watch'), 
      themeClasses: 'bg-yellow-50 text-yellow-600 border-yellow-200',
      tagClasses: 'bg-yellow-100/60 text-yellow-700 border-yellow-200'
    },
    { 
      key: 'release', 
      label: 'Release', 
      items: gradedMons.filter(item => item.scoreInfo.grade === 'release'), 
      themeClasses: 'bg-red-50 text-red-500 border-red-200',
      tagClasses: 'bg-red-100/60 text-red-600 border-red-200'
    }
  ];

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl w-[95vw] md:w-11/12 max-w-4xl max-h-[90vh] overflow-hidden flex flex-col ring-1 ring-gray-900/5 relative"
        onClick={(e) => e.stopPropagation()} 
      >
        {/* DARK HEADER */}
        <div className="bg-gray-800 p-3 md:p-4 flex justify-between items-center text-white shrink-0 shadow-md z-40">
          <div className="flex items-center gap-2">
            <Award size={20} className="text-indigo-400" />
            <h2 className="text-lg md:text-xl font-black tracking-tight">Grade Pokémon</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="p-1.5 md:p-2 bg-gray-700 hover:bg-red-500 rounded-lg transition-colors text-gray-300 hover:text-white shadow-sm" aria-label="Close modal">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* TABS BAR */}
        <div className="flex flex-wrap items-center gap-2 md:gap-3 w-full bg-gray-50 p-3 border-b border-gray-200 shadow-sm z-30 shrink-0">
          <div className="flex items-center bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm shrink-0">
            {ROLES.map((r, idx) => (
              <React.Fragment key={r.id}>
                <button
                  className={`px-4 py-2 text-xs md:text-sm font-bold transition-colors ${role === r.id ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                  onClick={() => setRole(r.id)}
                >
                  {r.label}
                </button>
                {idx < ROLES.length - 1 && <div className="w-px h-4 bg-gray-200"></div>}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* SCROLLABLE BODY / RESULTS AREA */}
        <div className="p-4 md:p-6 overflow-y-auto custom-scrollbar flex flex-col gap-3 md:gap-4 bg-gray-50 flex-1">
          {gradedMons.length === 0 ? (
            <div className="flex-1 bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col items-center justify-center min-h-[400px] text-gray-400 p-8 text-center gap-2">
              <span className="text-4xl">📊</span>
              <span className="font-bold">No Pokémon selected to grade.</span>
            </div>
          ) : (
            sections.map(section => {
              if (section.items.length === 0) return null;
              return (
                <div key={section.key} className="flex flex-col gap-2">
                  <div className={`px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest flex items-center justify-between border shadow-sm ${section.themeClasses}`}>
                    <span>{section.label}</span>
                    <span>{section.items.length} Pokémon</span>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    {section.items.map((item, idx) => (
                      <div key={item.mon.uuid || idx} className="bg-white p-3 md:p-4 rounded-xl border border-gray-200 shadow-sm flex items-start gap-3 md:gap-4 transition-transform hover:-translate-y-0.5">
                        <img 
                          src={`/images/pokemon/${item.species.id}${item.mon.is_shiny ? '_shiny' : ''}.png`} 
                          alt={item.species.name}
                          className="w-12 h-12 md:w-14 md:h-14 object-contain drop-shadow-md shrink-0"
                          onError={(e) => { e.target.src = '/images/pokemon/unknown.png'; }}
                        />
                        <div className="flex flex-col min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="font-bold text-gray-800 text-base md:text-lg truncate">
                              {item.mon.nickname || item.species.name}
                            </span>
                            {item.mon.is_shiny && <Sparkles size={14} className="text-indigo-600 fill-indigo-100 shrink-0" />}
                            <span className="text-indigo-600 bg-indigo-100/50 border border-indigo-200 px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0">
                              Lv {item.mon.level}
                            </span>
                            {(item.scoreInfo.ceilingSubskillScore - item.scoreInfo.currentSubskillScore) > 3 ? (
                              <span className="text-indigo-700 bg-indigo-100 border border-indigo-200 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase shrink-0" title="Has strong locked subskills">
                                Developing
                              </span>
                            ) : (
                              <span className="text-gray-600 bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase shrink-0" title="What you see is roughly what you get">
                                Proven
                              </span>
                            )}
                          </div>
                          
                          {item.scoreInfo.flags && item.scoreInfo.flags.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                              {item.scoreInfo.flags.map((flag, fIdx) => (
                                <span key={fIdx} className={`text-[10px] font-bold px-2 py-1 rounded-md border ${section.tagClasses}`}>
                                  {flag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}